// Centralized Web Audio API service with Tone.js

import * as Tone from 'tone';
import { SynthService } from './synthService';
import { userVolumeToGain } from './audioVolumeAdapter';
import { createLogger } from '@airhead-games/shared/utilities';
const logger = createLogger('webAudioService');
export class WebAudioService {
  private _context: AudioContext | null = null;

  // Separate analyzers and gains for each audio type
  private _speechAnalyzer: AnalyserNode | null = null;
  private _musicAnalyzer: AnalyserNode | null = null;
  private _sfxAnalyzer: AnalyserNode | null = null;

  private _speechGain: GainNode | null = null;
  private _musicGain: GainNode | null = null;
  private _sfxGain: GainNode | null = null;

  private _masterGain: GainNode | null = null;

  private _connectedElements: Map<
    HTMLAudioElement,
    { source: MediaElementAudioSourceNode; type: 'speech' | 'music' | 'sfx' }
  > = new Map();

  // Reference to SynthService
  private _synthService: SynthService | null = null;

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    try {
      // Initialize Web Audio API
      const AudioContextClass =
        window.AudioContext ||
        (window as Window & { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;

      if (!AudioContextClass) {
        logger.warn('Web Audio API is not supported in this browser');
        return;
      }

      this._context = new AudioContextClass();

      // Create separate analyzers for each audio type
      this._speechAnalyzer = this._context.createAnalyser();
      this._speechAnalyzer.fftSize = 2048;

      this._musicAnalyzer = this._context.createAnalyser();
      this._musicAnalyzer.fftSize = 2048;

      this._sfxAnalyzer = this._context.createAnalyser();
      this._sfxAnalyzer.fftSize = 2048;

      // Create separate gain nodes for individual volume control
      this._speechGain = this._context.createGain();
      this._musicGain = this._context.createGain();
      this._sfxGain = this._context.createGain();

      // Create master gain node for global muting
      this._masterGain = this._context.createGain();

      // Connect the audio graph: individual gains → master gain → destination
      // Analyzers are connected in parallel to preserve signal for visualization
      this._speechGain.connect(this._masterGain);
      this._musicGain.connect(this._masterGain);
      this._sfxGain.connect(this._masterGain);
      this._masterGain.connect(this._context.destination);

      // Initialize synthetic sound generator (connects to SFX gain)
      Tone.setContext(this._context);
      this._synthService = new SynthService(this._sfxGain);
      this._synthService.initialize();

      logger.debug(
        'WebAudioService initialized with separate gain nodes and analyzers'
      );
    } catch (error) {
      logger.error('Failed to initialize WebAudioService:', error);
    }
  }

  /**
   * Forward playNote calls to SynthService
   */
  playNote(semitones: number = 0): void {
    if (this._synthService) {
      this._synthService.playNote(semitones);
    } else {
      logger.warn('SynthService not initialized');
    }
  }

  /**
   * Get the SFX gain node for other services to connect to (e.g., SynthService)
   */
  getSfxGainNode(): GainNode | null {
    return this._sfxGain;
  }

  /**
   * Connect an audio element to the Web Audio graph
   * @param element The HTMLAudioElement to connect
   * @param type The type of audio (speech, music, or sfx)
   * @returns The created MediaElementAudioSourceNode or null if connection failed
   */
  connectSource(
    element: HTMLAudioElement | null,
    type: 'speech' | 'music' | 'sfx'
  ): MediaElementAudioSourceNode | null {
    if (!element || !this._context) {
      return null;
    }

    // Skip if already connected
    if (this._connectedElements.has(element)) {
      return this._connectedElements.get(element)?.source || null;
    }

    // Get the appropriate analyzer and gain for this audio type
    let analyzer: AnalyserNode | null = null;
    let gain: GainNode | null = null;

    switch (type) {
      case 'speech':
        analyzer = this._speechAnalyzer;
        gain = this._speechGain;
        break;
      case 'music':
        analyzer = this._musicAnalyzer;
        gain = this._musicGain;
        break;
      case 'sfx':
        analyzer = this._sfxAnalyzer;
        gain = this._sfxGain;
        break;
    }

    if (!analyzer || !gain) {
      logger.error(`Failed to get analyzer/gain nodes for type: ${type}`);
      return null;
    }

    try {
      const source = this._context.createMediaElementSource(element);

      if (type === 'speech') {
        // Special handling for speech: analyzer gets full signal, gain controls volume
        // source → analyzer (for visualization)
        // source → gain (for volume control)
        source.connect(analyzer);
        source.connect(gain);
      } else {
        // Normal flow for music/sfx: source → analyzer → gain
        source.connect(analyzer);
        analyzer.connect(gain);
      }

      this._connectedElements.set(element, { source, type });
      logger.debug(`Connected ${element.id} (${type}) to Web Audio graph`);
      return source;
    } catch (error) {
      logger.error(`Failed to connect ${type} audio source:`, error);
      return null;
    }
  }

  /**
   * Disconnect a previously connected audio element
   * @param element The HTMLAudioElement to disconnect
   */
  disconnectSource(element: HTMLAudioElement | null): void {
    if (!element || !this._connectedElements.has(element)) {
      return;
    }

    try {
      // Disconnect the source
      const connection = this._connectedElements.get(element);
      if (connection) {
        connection.source.disconnect();
        this._connectedElements.delete(element);
        logger.debug(
          `Disconnected ${connection.type} audio element from Web Audio graph`
        );
      }
    } catch (error) {
      logger.error('Failed to disconnect audio source:', error);
    }
  }

  /**
   * Set the mute state by adjusting the master gain node
   * @param isMuted True to mute, false to unmute
   */
  setMute(isMuted: boolean): void {
    if (!this._masterGain || !this._context) return;
    const volume = isMuted ? 0 : 1;
    const now = this._context.currentTime;
    this._masterGain.gain.cancelScheduledValues(now);
    this._masterGain.gain.setValueAtTime(this._masterGain.gain.value, now);
    this._masterGain.gain.linearRampToValueAtTime(volume, now + 0.05);
    logger.info(`WebAudioService global mute state set to ${isMuted}`);
  }

  /**
   * Set individual volume levels for each audio type
   * @param volumes Object with volume levels for speech, music, and/or sfx
   */
  setVolumes(
    volumes: Partial<{
      speechVolume: number;
      musicVolume: number;
      sfxVolume: number;
    }>
  ): void {
    if (!this._context) return;

    const now = this._context.currentTime;

    if (volumes.speechVolume !== undefined && this._speechGain) {
      const speechGain = userVolumeToGain('speechVolume', volumes.speechVolume);
      this._speechGain.gain.cancelScheduledValues(now);
      this._speechGain.gain.setValueAtTime(this._speechGain.gain.value, now);
      this._speechGain.gain.linearRampToValueAtTime(speechGain, now + 0.05);
    }

    if (volumes.musicVolume !== undefined && this._musicGain) {
      const musicGain = userVolumeToGain('musicVolume', volumes.musicVolume);
      this._musicGain.gain.cancelScheduledValues(now);
      this._musicGain.gain.setValueAtTime(this._musicGain.gain.value, now);
      this._musicGain.gain.linearRampToValueAtTime(musicGain, now + 0.05);
    }

    if (volumes.sfxVolume !== undefined && this._sfxGain) {
      const sfxGain = userVolumeToGain('sfxVolume', volumes.sfxVolume);
      this._sfxGain.gain.cancelScheduledValues(now);
      this._sfxGain.gain.setValueAtTime(this._sfxGain.gain.value, now);
      this._sfxGain.gain.linearRampToValueAtTime(sfxGain, now + 0.05);
    }
  }

  /**
   * Resume the audio context (required after user interaction in some browsers)
   */
  async resume(): Promise<void> {
    if (this._context && this._context.state !== 'running') {
      try {
        await this._context.resume();
        await Tone.start();
        logger.debug('Audio context resumed successfully');
      } catch (error) {
        logger.error('Failed to resume audio context:', error);
        throw error;
      }
    }
  }

  /**
   * Get the analyzer node for a specific audio type
   */
  getAnalyzer(type: 'speech' | 'music' | 'sfx'): AnalyserNode | null {
    switch (type) {
      case 'speech':
        return this._speechAnalyzer;
      case 'music':
        return this._musicAnalyzer;
      case 'sfx':
        return this._sfxAnalyzer;
      default:
        return null;
    }
  }

  /**
   * Get all analyzers for visualization
   */
  getAllAnalyzers(): {
    speech: AnalyserNode | null;
    music: AnalyserNode | null;
    sfx: AnalyserNode | null;
  } {
    return {
      speech: this._speechAnalyzer,
      music: this._musicAnalyzer,
      sfx: this._sfxAnalyzer,
    };
  }

  /**
   * Get the audio context
   */
  getContext(): AudioContext | null {
    return this._context;
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    // Clean up audio connections
    this._connectedElements.forEach((connection) =>
      connection.source.disconnect()
    );
    this._connectedElements.clear();

    // Dispose of the synth service
    if (this._synthService) {
      this._synthService.dispose();
      this._synthService = null;
    }

    // Close audio context
    if (this._context && this._context.state !== 'closed') {
      this._context
        .close()
        .catch((err) => logger.error('Error closing audio context:', err));
    }

    // Clean up Tone.js
    try {
      Tone.getContext().dispose();
    } catch (error) {
      logger.error('Error cleaning up Tone.js:', error);
    }

    // Clear references
    this._context = null;
    this._speechAnalyzer = null;
    this._musicAnalyzer = null;
    this._sfxAnalyzer = null;
    this._speechGain = null;
    this._musicGain = null;
    this._sfxGain = null;
    this._masterGain = null;
  }
}
